export type Platform = 'linux' | 'macos' | 'unknown' | 'windows';

/**
 * Detects the user's operating system and returns the appropriate modifier key
 * for keyboard shortcuts.
 *
 * @returns '⌘' for macOS, 'Ctrl' for Windows/Linux/Unknown
 */
export function getModifierKey(): string {
  const platform = getPlatform();

  return platform === 'macos' ? '⌘' : 'Ctrl';
}

/**
 * Detects the user's operating system platform
 *
 * @returns Platform type: 'macos', 'windows', 'linux', or 'unknown'
 */
export function getPlatform(): Platform {
  if (typeof navigator === 'undefined') {
    return 'unknown';
  }

  // Use modern userAgentData API if available
  if ('userAgentData' in navigator && navigator.userAgentData) {
    const platform = (navigator.userAgentData as { platform?: string }).platform;

    if (platform) {
      const platformLower = platform.toLowerCase();

      if (platformLower.includes('mac')) return 'macos';

      if (platformLower.includes('win')) return 'windows';

      if (platformLower.includes('linux')) return 'linux';
    }
  }

  // Fallback to userAgent
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes('mac os x') || userAgent.includes('macintosh')) return 'macos';

  if (userAgent.includes('win')) return 'windows';

  if (userAgent.includes('linux')) return 'linux';

  return 'unknown';
}

/**
 * Detects if the current platform is macOS
 *
 * @returns true if running on macOS, false otherwise
 */
export function isMacOS(): boolean {
  return getPlatform() === 'macos';
}
